import { UserApiService } from '@admin/core';
import { PlanCreate, PlanEntity, PlanStatus, PlanUpdate } from '@admin/core/models';
import { PlanStatusValues } from '@admin/shared/data';
import { dateToString } from '@admin/shared/helpers';
import { formControl, Validators } from '@al00x/forms';
import { Component, inject } from '@angular/core';
import { CacBaseDialogComponent, CacDialogLayoutComponent, CacFormBuilderComponent, formBuilder, PermissionService } from '@controladad/ng-base';
import { TreeViewComponent } from "@admin/shared";

export type PlanDialogData = PlanEntity;

export type PlanDialogResult = PlanCreate | PlanUpdate;

@Component({
  selector: 'app-plan-dialog',
  imports: [CacDialogLayoutComponent, CacFormBuilderComponent, TreeViewComponent],
  templateUrl: './plan-dialog.component.html',
})
export class PlanDialogComponent extends CacBaseDialogComponent<PlanDialogData, PlanDialogResult> {
  private readonly userApi = inject(UserApiService);
  private readonly permission = inject(PermissionService);

  formBuilder = formBuilder({
    cols: 2,
    inputs: {
      name: {
        control: formControl<string>(undefined, Validators.required),
        label: 'نام برنامه',
        colspan: 2,
      },
      startDate: {
        control: formControl<string | null>(undefined),
        label: 'تاریخ شروع',
        type: 'datetime',
      },
      endDate: {
        control: formControl<string | null>(undefined),
        label: 'تاریخ پایان',
        type: 'datetime',
      },
      userId: {
        control: formControl<number>(undefined, Validators.required),
        label: 'کاربر',
        type: 'select',
        items: this.userApi.getUserItems(),
      },
      status: {
        control: formControl<PlanStatus>(undefined, Validators.required),
        label: 'وضعیت',
        type: 'select',
        items: PlanStatusValues,
      },
      createdAt: {
        control: formControl<string>(undefined),
        label: 'تاریخ ایجاد',
        type: 'datetime',
        hidden: !this.data,
      },
      isAutomatic: {
        control: formControl<boolean>(false, Validators.required),
        label: 'شروع و پایان خودکار',
        type: 'switch',
      },
    },
    onInit: () => {
      if (this.data?.status === PlanStatus.Started) {
        this.formBuilder.setReadonly(true);
        this.formBuilder.setReadonly(false, 'status');
      }
      if (this.data && !this.permission.hasPermission('ChangePlanStatus')) {
        this.formBuilder.setReadonly(true, 'status');
      }
      this.formBuilder.setReadonly(true, 'createdAt');
    },
  });

  locationControl = formControl<number[] | undefined>(undefined);

  constructor() {
    super();
    const plan = this.data;
    if (plan) {
      this.formBuilder.setValue({
        ...plan,
        userId: plan.users.at(0)?.id!,
      });
      this.locationControl.setValue(plan.locations.map((x) => x.id));
    }
  }

  onSubmit() {
    const values = this.formBuilder.getValue();
    if (!values) return;

    const status = values.status;
    if (status === PlanStatus.Closed) {
      snackbar$.error('وضعیت برنامه به بسته شده قابل تغییر نیست');
      return;
    }
    if (values.isAutomatic && status !== PlanStatus.Pending) {
      snackbar$.error('وضعیت برنامه در حالت خودکار فقط به در انتظار قابل تغییر است');
      return;
    }

    const locationIds = this.locationControl.value;
    if (!locationIds?.length) {
      snackbar$.error('لطفا حداقل یک مکان انتخاب نمایید');
      return;
    }

    const { userId, createdAt, ...model } = values;
    this.submit({
      ...model,
      locationIds,
      userIds: [userId],
      startDate: dateToString(values.startDate),
      endDate: dateToString(values.endDate),
    });
  }
}
